<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.1 — top-centre incoming-call toast: contact, inbox,
// Decline (round ruby) and Answer (brand). While the agent is already on a
// call, Answer becomes "Hold + answer". Further ringing calls collapse into a
// "+N more" line under the first one.
//
// Warm Paper (contract "Alerts / banners / callouts"): toasts are the
// near-black attention card — ink in light, a bar just under the ground in
// dark — with the icon in a small light circle (kaptionCallPaper.PAPER_TOAST).
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PAPER_TOAST } from './kaptionCallPaper';

const props = defineProps({
  calls: { type: Array, default: () => [] },
  onCall: { type: Boolean, default: false },
  busyId: { type: [Number, String], default: null },
  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ownership.UI.4: below
  // the pass toast when both show.
  stacked: { type: Boolean, default: false },
});

const emit = defineEmits(['answer', 'decline']);

const call = computed(() => props.calls[0] || null);
const moreCount = computed(() => Math.max(0, props.calls.length - 1));
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
    enter-from-class="-translate-y-4 opacity-0"
    leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
    leave-to-class="-translate-y-4 opacity-0"
  >
    <div
      v-if="call"
      data-testid="kaption-incoming-call-toast"
      role="alertdialog"
      :aria-label="$t('KAPTION_CALLS.TOAST.INCOMING')"
      :class="[stacked ? 'top-32' : 'top-3', PAPER_TOAST]"
      class="fixed left-1/2 z-[60] w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 px-4 py-3"
    >
      <div class="flex items-center gap-3">
        <div class="relative shrink-0">
          <Avatar
            :name="call.contactName || '?'"
            :src="call.contactAvatar"
            :size="40"
            rounded-full
          />
          <span
            class="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-[#f2f2f3] ring-2 ring-[#16181b] dark:ring-[#0c0c0d]"
          >
            <span
              class="i-lucide-phone-incoming size-2.5 animate-pulse text-n-teal-10"
            />
          </span>
        </div>
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-xs leading-tight text-white/55">
            {{ $t('KAPTION_CALLS.TOAST.INCOMING') }}
          </span>
          <span
            data-testid="kaption-toast-contact"
            class="mt-1 truncate text-sm font-medium text-white"
          >
            {{ call.contactName }}
          </span>
          <span v-if="call.inboxName" class="truncate text-xs text-white/60">
            {{ $t('KAPTION_CALLS.TOAST.VIA', { inbox: call.inboxName }) }}
          </span>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <Button
            data-testid="kaption-toast-decline"
            icon="i-lucide-phone-off"
            color="ruby"
            size="md"
            class="!rounded-full"
            :aria-label="$t('KAPTION_CALLS.TOAST.DECLINE')"
            :disabled="busyId === call.id"
            @click="emit('decline', call.id)"
          />
          <Button
            data-testid="kaption-toast-answer"
            :icon="onCall ? 'i-lucide-pause' : 'i-lucide-phone'"
            :label="
              onCall
                ? $t('KAPTION_CALLS.TOAST.HOLD_AND_ANSWER')
                : $t('KAPTION_CALLS.TOAST.ANSWER')
            "
            color="teal"
            size="md"
            :is-loading="busyId === call.id"
            @click="emit('answer', call.id)"
          />
        </div>
      </div>
      <p
        v-if="moreCount"
        data-testid="kaption-toast-more"
        class="mb-0 mt-2.5 border-t border-white/10 pt-2 text-xs text-white/60"
      >
        {{ $t('KAPTION_CALLS.TOAST.MORE_WAITING', { count: moreCount }) }}
      </p>
    </div>
  </Transition>
</template>
